import React from 'react';
import { Card, Row, Col, Typography, Badge, Button, Space } from 'antd';
import { SettingOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import SEOMetadata, { seoConfigs } from './SEOMetadata';

const { Title, Text } = Typography;

const AppSelection = () => {
  const navigate = useNavigate();
  const { user, loading, getUserName } = useAuth();

  const apps = [
    {
      id: 'parayana-counter',
      title: 'Parayana Counter',
      description: 'Track your daily prayers and mantras',
      icon: '🕉️',
      color: '#FF8C00',
      route: '/apps/prayer-counter',
      available: true
    },
    {
      id: 'pradakshina-counter',
      title: 'Pradakshina Counter',
      description: 'Count temple circumambulations',
      icon: '🔄',
      color: '#FF7F00',
      route: '/apps/pradakshina-counter',
      available: false
    },
    {
      id: 'my-library',
      title: 'My Library',
      description: 'Store spiritual books and texts',
      icon: '📚',
      color: '#FFB347',
      route: '/apps/library',
      available: false
    },
    {
      id: 'temple-checklist',
      title: 'Temple Visit Checklist',
      description: 'Track temple visits and rituals',
      icon: '🏛️',
      color: '#FF9500',
      route: '/apps/temple-checklist',
      available: false
    },
    {
      id: 'random-counter',
      title: 'Random Counter',
      description: 'General purpose counting tool',
      icon: '🔢',
      color: '#FF8C00',
      route: '/apps/random-counter',
      available: true
    }
  ];

  const handleAppClick = (app) => {
    if (app.available) {
      navigate(app.route);
    }
  };

  // Show loading while checking authentication
  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        height: '400px'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🕉️</div>
          <div style={{ color: '#FF8C00', fontSize: '16px' }}>Loading...</div>
        </div>
      </div>
    );
  }

  // Only render if user is authenticated (AuthGuard handles this at app level)
  if (!user) {
    return null;
  }

  const userName = getUserName();

  return (
    <div style={{ padding: '16px', maxWidth: '1200px', margin: '0 auto' }}>
      <SEOMetadata {...seoConfigs.home} />
      
      <Card
        style={{ 
          marginBottom: 16,
          background: 'rgba(255, 255, 255, 0.95)',
          border: '1px solid #FFB347',
          borderRadius: '8px'
        }}
        styles={{ body: { padding: '12px 16px' } }}
      >
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center'
        }}>
          <Text style={{ color: '#FF8C00', fontWeight: '500' }}>
            {userName ? `Welcome ${userName} to your spiritual journey! 🙏` : 'Welcome to your spiritual journey! 🙏'}
          </Text>
          <Space>
            <Button
              icon={<SettingOutlined />}
              onClick={() => navigate('/settings')}
              style={{
                borderColor: '#FFB347',
                color: '#FF8C00'
              }}
              size="small"
            >
              Settings
            </Button>
          </Space>
        </div>
      </Card>
      
      <div style={{ textAlign: 'center', marginBottom: '32px', paddingTop: '20px' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>🕉️</div>
        <Title level={1} style={{ 
          color: '#FF8C00', 
          marginBottom: '8px',
          fontSize: 'clamp(24px, 5vw, 32px)'
        }}>
          ProgressCraft
        </Title>
        <Text style={{ 
          color: '#666', 
          fontSize: '16px',
          display: 'block'
        }}>
          Your Spiritual Journey Companion
        </Text>
      </div>

      <Row gutter={[16, 16]} justify="center">
        {apps.map((app) => (
          <Col 
            key={app.id} 
            xs={24} 
            sm={12} 
            md={8} 
            lg={6}
            style={{ display: 'flex' }}
          >
            <Card
              hoverable={app.available}
              onClick={() => handleAppClick(app)}
              style={{
                width: '100%',
                height: '180px',
                borderRadius: '16px',
                border: `2px solid ${app.available ? app.color : '#e8e8e8'}`,
                boxShadow: app.available 
                  ? `0 4px 12px ${app.color}30`
                  : '0 2px 8px #f0f0f0',
                cursor: app.available ? 'pointer' : 'not-allowed',
                opacity: app.available ? 1 : 0.6,
                transition: 'all 0.3s ease',
                display: 'flex',
                flexDirection: 'column'
              }}
              styles={{
                body: {
                  padding: '20px',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  textAlign: 'center'
                }
              }}
            >
              <div style={{ 
                fontSize: '40px', 
                marginBottom: '12px',
                filter: app.available ? 'none' : 'grayscale(100%)'
              }}>
                {app.icon}
              </div>
              
              <Title level={4} style={{ 
                margin: '0 0 8px 0', 
                color: app.available ? app.color : '#999',
                fontSize: '16px',
                fontWeight: 'bold'
              }}>
                {app.title}
                {!app.available && (
                  <Badge 
                    count="Soon" 
                    style={{ 
                      backgroundColor: '#f5f5f5', 
                      color: '#666',
                      marginLeft: '8px',
                      fontSize: '10px'
                    }} 
                  />
                )}
              </Title>
              
              <Text style={{ 
                color: app.available ? '#666' : '#999', 
                fontSize: '12px',
                lineHeight: '1.4'
              }}>
                {app.description}
              </Text>
            </Card>
          </Col>
        ))}
      </Row>

      <div style={{ 
        textAlign: 'center', 
        marginTop: '40px',
        padding: '24px',
        background: 'linear-gradient(135deg, #FFF8F0 0%, #FFF4E6 100%)',
        borderRadius: '16px',
        border: '1px solid #FFE5B4'
      }}>
        <div style={{ fontSize: '24px', marginBottom: '12px' }}>🙏</div>
        <Text style={{ 
          color: '#FF8C00', 
          fontSize: '14px',
          fontStyle: 'italic'
        }}>
          "Progress in spiritual practice comes through consistent daily efforts"
        </Text>
      </div>
    </div>
  );
};

export default AppSelection; 